import React, { useState, useEffect } from "react";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import { serverUrl } from "../../../../../nestserver";
import moment from "moment";
import {
  Form,
  Input,
  Select,
  Button,
  Table,
  Popconfirm,
  Tag,
  Row,
  Col,
  Card,
  Typography,
  Tooltip,
  Empty,
  Modal,
} from "antd";
import {
  CalendarOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { message } from "antd";

const { Title } = Typography;
const { Option } = Select;

const standardHolidays = [
  { name: "New Year's Day", type: "public", category: "national" },
  { name: "Pongal", type: "festival", category: "regional" },
  { name: "Makar Sankranti", type: "festival", category: "national" },
  { name: "Thiruvalluvar Day", type: "public", category: "regional" },
  { name: "Uzhavar Thirunal", type: "festival", category: "regional" },
  { name: "Republic Day", type: "public", category: "national" },
  { name: "Maha Shivaratri", type: "religious", category: "national" },
  { name: "Holi", type: "festival", category: "national" },
  { name: "Ugadi", type: "festival", category: "regional" },
  { name: "Good Friday", type: "religious", category: "national" },
  { name: "Tamil New Year", type: "festival", category: "regional" },
  { name: "Vishu", type: "festival", category: "regional" },
  { name: "Eid al-Fitr", type: "religious", category: "national" },
  { name: "Independence Day", type: "public", category: "national" },
  { name: "Onam", type: "festival", category: "regional" },
  { name: "Raksha Bandhan", type: "festival", category: "national" },
  { name: "Janmashtami", type: "religious", category: "national" },
  { name: "Ganesh Chaturthi", type: "religious", category: "national" },
  { name: "Gandhi Jayanti", type: "public", category: "national" },
  { name: "Ayudha Puja", type: "festival", category: "regional" },
  { name: "Dussehra", type: "festival", category: "national" },
  { name: "Diwali", type: "festival", category: "national" },
  { name: "Christmas Day", type: "religious", category: "national" },
];

const currentYear = moment().year();

const CreateHoliday = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchHolidays = async () => {
    setTableLoading(true);
    try {
      const res = await axios({
        method: "get",
        url: `${serverUrl}/holiday?year=${currentYear}`,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      const explicitHolidays = res.data.filter((h) => !h.isDefault);
      setHolidays(explicitHolidays);
    } catch (error) {
      console.error("Error fetching holidays:", error);
      message.error("Failed to load holidays");
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const handleStandardHolidayChange = (value) => {
    if (value) {
      const selectedHoliday = standardHolidays.find((h) => h.name === value);
      if (selectedHoliday) {
        form.setFieldsValue({
          name: selectedHoliday.name,
          type: selectedHoliday.type,
          category: selectedHoliday.category,
        });
      }
    } else {
      form.setFieldsValue({ name: "" });
    }
  };

  const onFinish = async (values) => {
    if (!values.dateRange || values.dateRange.length !== 2) {
      return message.error("Please select a date range");
    }

    const formData = {
      name: values.name,
      from: values.dateRange[0].format("YYYY-MM-DD"),
      to: values.dateRange[1].format("YYYY-MM-DD"),
      type: values.type,
      category: values.category,
    };

    setLoading(true);
    try {
      await axios({
        method: "post",
        url: `${serverUrl}/holiday`,
        data: formData,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      message.success("Holiday created successfully");
      form.resetFields();
      form.setFieldsValue({ type: "public", category: "national" });
      setCreateModalOpen(false);
      fetchHolidays();
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || "Failed to create holiday";
      if (Array.isArray(msg)) {
        message.error(msg[0]);
      } else {
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios({
        method: "delete",
        url: `${serverUrl}/holiday/${id}`,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      message.success("Holiday deleted successfully");
      fetchHolidays();
    } catch (error) {
      message.error("Failed to delete holiday");
    }
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <span className="font-semibold text-gray-800">{text}</span>
      ),
    },
    {
      title: "Duration",
      key: "duration",
      align: "center",
      render: (_, record) => (
        <span className="inline-flex items-center gap-2 text-gray-600">
          <CalendarOutlined className="text-gray-400" style={{ marginRight: 2 }} />
          {moment(record.from).format("MMM DD")}
          {record.to !== record.from
            ? ` - ${moment(record.to).format("MMM DD")}`
            : ""}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      align: "center",
      render: (type) => {
        let color =
          type === "public" ? "blue" : type === "festival" ? "green" : "purple";
        return (
          <Tag color={color} className="capitalize">
            {type}
          </Tag>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      align: "center",
      render: (category) => (
        <Tag
          color={category === "national" ? "red" : "orange"}
          className="capitalize"
        >
          {category}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Popconfirm
          title="Delete the holiday"
          description={`Are you sure to delete ${record.name}?`}
          onConfirm={() => handleDelete(record._id)}
          okText="Yes"
          cancelText="No"
        >
          <Tooltip title="Delete">
            <Button
              danger
              type="primary"
              shape="circle"
              className="flex items-center justify-center"
              icon={
                <DeleteOutlined
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              }
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="mt-8">
      <Card
        bordered={false}
        className="shadow-none"
        bodyStyle={{ padding: "24px" }}
      >
        <div className="flex items-center justify-between pb-6">
          <div className="flex items-center gap-3">
            <Title level={4} style={{ margin: 0 }}>
              Holidays in {currentYear}
            </Title>
            {holidays.length > 0 && (
              <Tag color="blue" className="rounded-full">
                {holidays.length}
              </Tag>
            )}
          </div>
          <Button
            type="primary"
            size="large"
            className="inline-flex items-center gap-2"
            onClick={() => setCreateModalOpen(true)}
          >
            Add Holiday
          </Button>
        </div>

        <Table
          columns={tableColumns}
          dataSource={holidays}
          rowKey="_id"
          loading={tableLoading}
          pagination={{ pageSize: 7 }}
          size="middle"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No holidays created yet"
              />
            ),
          }}
        />
      </Card>

      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            Create Holiday
          </Title>
        }
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="pt-4"
          initialValues={{
            type: "public",
            category: "national",
            standardHoliday: "",
          }}
        >
          <Form.Item
            name="standardHoliday"
            label={
              <span className="font-medium text-gray-600">
                Standard Holiday (Auto-fill)
              </span>
            }
          >
            <Select
              placeholder="-- Custom Holiday --"
              onChange={handleStandardHolidayChange}
              allowClear
              size="large"
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              <Option value="">-- Custom Holiday --</Option>
              {standardHolidays.map((holiday, idx) => (
                <Option key={idx} value={holiday.name}>
                  {holiday.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label={
              <span className="font-medium text-gray-600">Holiday Name</span>
            }
            rules={[
              { required: true, message: "Please enter the holiday name" },
            ]}
          >
            <Input placeholder="e.g. Summer Vacation" size="large" />
          </Form.Item>

          <Form.Item
            name="dateRange"
            label={
              <span className="font-medium text-gray-600">Date Range</span>
            }
            rules={[
              {
                required: true,
                message: "Please select from and to dates",
              },
            ]}
          >
            <DatePicker.RangePicker style={{ width: "100%" }} size="large" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label={<span className="font-medium text-gray-600">Type</span>}
                rules={[{ required: true }]}
              >
                <Select size="large" getPopupContainer={(trigger) => trigger.parentNode}>
                  <Option value="public">Public</Option>
                  <Option value="festival">Festival</Option>
                  <Option value="religious">Religious</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label={
                  <span className="font-medium text-gray-600">Category</span>
                }
                rules={[{ required: true }]}
              >
                <Select size="large" getPopupContainer={(trigger) => trigger.parentNode}>
                  <Option value="national">National</Option>
                  <Option value="regional">Regional</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              size="large"
              onClick={() => setCreateModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} size="large">
              Create Holiday
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default CreateHoliday;
